import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

export interface BreakdownDataPoint {
    group: string;
    value: number;
}

interface BreakdownChartProps {
    data: BreakdownDataPoint[];
    // Maps a raw group key to its display label
    groupLabels: Record<string, string>;
    height?: number;
    color?: string;
    yFormatter?: (value: number) => string;
}

// Renders the rounded, dark tooltip shown on hover
const CustomTooltip: React.FC<any> = ({ active, payload, yFormatter }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const point = payload[0];
    return (
        <div className='time-series-tooltip'>
            <div className='tooltip-row'>
                <span className='tooltip-dot' style={{ backgroundColor: point.fill }} />
                <span className='tooltip-name'>{point.payload.label}</span>
                <span className='tooltip-value'>{yFormatter ? yFormatter(point.value) : point.value}</span>
            </div>
        </div>
    );
};

const BreakdownChart: React.FC<BreakdownChartProps> = ({
    data, groupLabels, height = 220, color = 'var(--primary-color)', yFormatter,
}) => {
    // Resolve the display label per data point once, for the axis and tooltip
    const chartData = data.map(point => ({ ...point, label: groupLabels[point.group] ?? point.group }));

    return (
        <ResponsiveContainer width='100%' height={height}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='var(--border-color-light)' />
                <XAxis
                    dataKey='label'
                    tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                    axisLine={{ stroke: 'var(--border-color-light)' }}
                    tickLine={false}
                    interval={0}
                    angle={-35}
                    textAnchor='end'
                    height={50}
                />
                <YAxis
                    tickFormatter={yFormatter}
                    tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip yFormatter={yFormatter} />} cursor={{ fill: 'var(--background-color-gray)' }} />
                <Bar dataKey='value' radius={[4, 4, 0, 0]} isAnimationActive animationDuration={400}>
                    {chartData.map(point => (
                        <Cell key={point.group} fill={color} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default BreakdownChart;
